import type { Tournament, TournamentOfChampions } from './types';

/**
 * Which tournament the player-facing store should render.
 *
 * Tri-state, because "no tournament" has two different meanings for the caller:
 * - a `Tournament` — render it
 * - `null`         — nothing to render, but the player still has tournament state (a TOC
 *                    payload); keep the store's own slot cleared without wiping surrounding
 *                    state
 * - `undefined`    — the player has nothing at all; clear the store
 */
export type PlayerTournamentSelection = Tournament | null | undefined;

/**
 * Pure selection rule, shared by regular tournaments and Tournament of Champions.
 *
 * This lived in `TournamentStore.performFetch` until #2281 moved it into
 * `TournamentOfChampionsStore.ingestFetchResult` and inverted the priority on the way, which
 * stopped unclaimed rounds from ever being rendered or claimed (fixed in #1117's spirit by the
 * `isPreviousRoundUnclaimed` branch below). It is back out here on purpose: the rule decides
 * what EVERY player sees, TOC or not, so it must not sit inside a feature-specific store where
 * a TOC change can alter regular races as a side effect.
 */
export const selectPlayerTournament = ({
  currentTournament,
  previousTournament,
  tournamentOfChampions,
  coinType,
}: {
  currentTournament: Tournament | null;
  previousTournament: Tournament | null;
  tournamentOfChampions: TournamentOfChampions | null;
  coinType: 'gc' | 'sc';
}): PlayerTournamentSelection => {
  // An ended round the player never claimed outranks the round they are seated in now: the store
  // holds a single `currentTournament`, so whatever loses here is never rendered and its prize is
  // never claimed. Narrower than the pre-#2281 rule on purpose — only an *unclaimed* previous
  // round wins, so a settled one can no longer preempt an active race.
  const isPreviousRoundUnclaimed =
    previousTournament?.status === 'finished' && previousTournament.playerStatus === 'started';

  if (!tournamentOfChampions && isPreviousRoundUnclaimed && previousTournament?.leaderboards[coinType]) {
    return previousTournament;
  }
  if (currentTournament?.leaderboards[coinType]) {
    return currentTournament;
  }
  if (!tournamentOfChampions && previousTournament?.leaderboards[coinType]) {
    return previousTournament;
  }
  if (tournamentOfChampions) {
    return null;
  }
  return undefined;
};
